<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useStorage } from '@vueuse/core'
import PatternLock from '@phenax/pattern-lock-js'
import { useEventStore } from "@/stores/EventsStore"
import emitter from '@/utils/emitter'
import { useTheme } from "vuetify/framework"

const props = defineProps({
  width: { type: Number, default: 320 },
  height: { type: Number, default: 420 },
  gridX: { type: Number, default: 3 },
  gridY: { type: Number, default: 3 },
  correctHashes: { type: Array, default: () => ['LTExNjI0MjcxOTA='] }, // narysuj litere "L"
})

const emit = defineEmits([
  'ready',
  'complete',            // (hash, transactionOk)
  'success',             // (transactionOk)
  'failure',             // (transactionOk)
  'visibility-change',    // (visible)
])

const { global } = useTheme()

const eventStore = useEventStore()

const canvasEl = ref(null)
const visible = ref(false)
const transactionOk = ref(true) // default YES

const lockEventId = ref(null)
const showTransactionSuccessToggle = ref(false)
const transactionUrl = ref(null)

const panelScreenLocked = useStorage('panelScreenLocked', false)

let lock = null

const show = async () => {
  visible.value = true
  emit('visibility-change', true)

  panelScreenLocked.value = true

  await nextTick()
  initLock()
}

const hide = () => {
  visible.value = false
  emit('visibility-change', false)
  panelScreenLocked.value = false
  transactionOk.value = true

  destroyLock()
}

const initLock = () => {
  if (!canvasEl.value) return
  destroyLock()

  lock = PatternLock({
    $canvas: canvasEl.value,
    width: props.width,
    height: props.height,
    grid: [props.gridX, props.gridY],
  })

  //lock.setTheme(global.name.value)
  console.log('global.name.value', global.name.value)
  lock.setTheme({
    default: {
      colors: {
        accent: '#e84a07',     // Accent color for node
        primary: global.name.value === 'light' ? '#1e2024' : '#f7f7f9',    // Primary node and line color
        bg: global.name.value === 'light' ? '#f7f7f9' : '#1e2024',         // Canvas background color
      },
      dimens: {
        node_radius: 20,       // Radius of the outer ring of a node
        line_width: 6,         // Thickness of the line joining nodes
        node_core: 8,          // Radius of the inner circle of a node
        node_ring: 1,          // Outer ring thickness
      },
    },
    success: {
      colors: {
        accent: '#51e980',     // Green accent on successful match
      },
    },
    failure: {
      colors: {
        accent: '#e84a07',     // Red accent on an unsuccessful match
      },
    },
    customState: {                 // Your custom state
      dimens: {
        node_radius: 25,       // Increases the node radius
      },
    },
  })


  lock.onComplete(({ hash }) => {
    console.log('complete', hash, transactionOk.value)
    emit('complete', hash, transactionOk.value)
  })

  if (props.correctHashes?.length) {
    lock
      .matchHash(props.correctHashes)
      .onSuccess(() => {
        lock.setThemeState('success')
        emit('success', transactionOk.value)
        if(lockEventId.value && showTransactionSuccessToggle.value && transactionOk.value) {
          emitter.emit('pattern-unlocked', true)
        }
        hide()
      })
      .onFailure(() => {
        lock.setThemeState('failure')
        emit('failure', transactionOk.value)
      })
  }

  emit('ready', lock)
}

const destroyLock = () => {
  if (lock?.destroy) lock.destroy()
  lock = null
}

const retryTransaction = () => window.open(transactionUrl.value, '_blank')

const onShowLock = ({ show: shouldShow = false, eventId = null, showTransactionSuccessToggle: withToggle = false, transactionUrl: retryUrl = null } = {}) => {
  if (!shouldShow) return hide()

  lockEventId.value = eventId
  showTransactionSuccessToggle.value = withToggle
  transactionUrl.value = retryUrl
  show()
}

onMounted(() => {
  emitter.on('show-lock', onShowLock)

  if (panelScreenLocked.value) {
    show()
  }
})

onBeforeUnmount(() => {
  emitter.off('show-lock', onShowLock)
  destroyLock()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="lock-overlay"
      @touchmove.prevent
      @wheel.prevent
    >
      <div class="lock-card">
        <div
          v-if="transactionUrl"
          class="lock-controls d-flex flex-column align-center"
        >
          <VBtn
            class=""
            color="secondary"
            variant="outlined"
            size="small"
            @click="retryTransaction"
          >
            {{ $t('pattern_lock.retry_transaction') }}
          </VBtn>
        </div>

        <div class="lock-canvas-wrap">
          <canvas ref="canvasEl" />
        </div>

        <div
          v-if="showTransactionSuccessToggle"
          class="lock-controls d-flex flex-column align-center"
        >
          <div class="d-flex align-center">
            <div
              class="transaction-label mr-4 cursor-pointer"
              @click="transactionOk = !transactionOk"
            >
              {{ $t('pattern_lock.transaction_success') }}
            </div>
            <VSwitch
              v-model="transactionOk"
              class="transaction-switch"
              color="primary"
              inset
              hide-details
            >
              <template #label>
                <div class="switch-label">
                  <div class="switch-sub">
                    {{ transactionOk ? 'TAK' : 'NIE' }}
                  </div>
                </div>
              </template>
            </VSwitch>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
}

.lock-card {
  width: min(520px, 100%);
  border-radius: 16px;
  background: rgba(var(--v-theme-background));
  padding: 16px;
}

.lock-canvas-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.lock-controls {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

.transaction-label {
  font-size: 1.2em;
}

.transaction-switch {
  zoom: 1.3;
}

.switch-label {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.switch-sub {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
}
</style>
